if (!customElements.get('livana-product-gallery')) {
  customElements.define(
    'livana-product-gallery',
    class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.track = this.querySelector('.gallery-track');
        this.slides = [...this.querySelectorAll('.gallery-slide')];
        if (!this.track || !this.slides.length) return;
        this.controller = new AbortController();
        const signal = this.controller.signal;
        this.thumbnails = [...this.querySelectorAll('[data-slide]')];
        this.rail = this.querySelector('.gallery-thumbnails');
        this.arrows = [...this.querySelectorAll('[data-direction]')];
        this.count = this.querySelector('#gallery-count');
        this.activeSlide = -1;
        this.frame = 0;
        this.update();

        this.track.addEventListener(
          'scroll',
          () => {
            if (this.frame) return;
            this.frame = requestAnimationFrame(() => {
              this.frame = 0;
              this.update();
            });
          },
          { signal, passive: true },
        );
        this.thumbnails.forEach((button) => {
          button.addEventListener('click', () => this.showSlide(Number(button.dataset.slide)), {
            signal,
          });
        });
        this.arrows.forEach((button) => {
          button.addEventListener(
            'click',
            () => {
              this.showSlide(this.currentIndex() + Number(button.dataset.direction));
            },
            { signal },
          );
        });
        this.addEventListener(
          'keydown',
          (event) => {
            if (!event.target.closest('[data-slide], [data-direction]')) return;
            let index;
            switch (event.key) {
              case 'ArrowLeft':
                index = this.currentIndex() - 1;
                break;
              case 'ArrowRight':
                index = this.currentIndex() + 1;
                break;
              case 'Home':
                index = 0;
                break;
              case 'End':
                index = this.slides.length - 1;
                break;
              default:
                return;
            }
            event.preventDefault();
            index = this.clamp(index);
            this.showSlide(index);
            this.thumbnails[index]?.focus({ preventScroll: true });
          },
          { signal },
        );
        if (typeof ResizeObserver === 'function') {
          this.resizeObserver = new ResizeObserver(() => this.update());
          this.resizeObserver.observe(this.track);
        } else {
          window.addEventListener('resize', () => this.update(), { signal });
        }
      }

      clamp(index) {
        return Math.max(0, Math.min(this.slides.length - 1, index));
      }

      currentIndex() {
        const step = this.slides[1]
          ? this.slides[1].offsetLeft - this.slides[0].offsetLeft
          : this.track.clientWidth;
        return step ? this.clamp(Math.round(this.track.scrollLeft / step)) : 0;
      }

      setScrollPosition(element, left) {
        if (typeof element.scrollTo === 'function') element.scrollTo({ left });
        else element.scrollLeft = left;
      }

      showSlide(index) {
        const slide = this.slides[this.clamp(index)];
        this.setScrollPosition(this.track, slide.offsetLeft - this.slides[0].offsetLeft);
      }

      update() {
        const index = this.currentIndex();
        if (index === this.activeSlide) return;
        this.activeSlide = index;
        this.thumbnails.forEach((button, slideIndex) =>
          button.setAttribute('aria-pressed', String(slideIndex === index)),
        );
        this.arrows.forEach((button) => {
          button.disabled =
            Number(button.dataset.direction) < 0 ? index === 0 : index === this.slides.length - 1;
        });
        if (this.count) {
          this.count.textContent = `${String(index + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
        }
        this.slides.slice(Math.max(0, index - 1), index + 2).forEach((slide) => {
          const image = slide.querySelector('img');
          if (image) image.loading = 'eager';
        });
        const thumbnail = this.thumbnails[index];
        if (!this.rail || !thumbnail) return;
        const start = thumbnail.offsetLeft;
        const end = start + thumbnail.offsetWidth;
        if (start < this.rail.scrollLeft) {
          this.setScrollPosition(this.rail, start);
        } else if (end > this.rail.scrollLeft + this.rail.clientWidth) {
          this.setScrollPosition(this.rail, end - this.rail.clientWidth);
        }
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.controller = null;
        this.resizeObserver?.disconnect();
        cancelAnimationFrame(this.frame);
        this.frame = 0;
      }
    },
  );
}
